import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
  VariantUpdateFailureEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Shows or clears the request-in-progress state.
   * @param {boolean} isPending
   * @param {boolean} [enableWhenIdle]
   */
  setPending(isPending, enableWhenIdle = true) {
    this.refs.addToCartButton.toggleAttribute('aria-busy', isPending);
    if (isPending || !enableWhenIdle) {
      this.disable();
    } else {
      this.enable();
    }
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    // Success feedback is triggered only after the cart API confirms the add.
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {boolean} */
  #variantAvailable = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantUpdateFailure, this.#onVariantUpdateFailure, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    const initialAddToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    this.#variantAvailable = initialAddToCartButton ? !initialAddToCartButton.disabled : false;

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) return;

    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          if (this.#variantChangeInProgress || !this.#variantAvailable) return;
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    for (const container of allAddToCartContainers) {
      container.setPending(true);
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          for (const container of allAddToCartContainers) {
            container.setPending(this.#variantChangeInProgress, this.#variantAvailable);
            container.animateAddToCart();
            if (container.dataset.addToCartAnimation === 'true' && !container.closest('.quick-add-modal')) {
              container.animateFlyToCart();
            }
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        const message = 'We could not add this piece. Please try again.';
        this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message, message, error));
        this.#showAddError(message);
      })
      .finally(() => {
        for (const container of allAddToCartContainers) {
          container.setPending(this.#variantChangeInProgress, this.#variantAvailable);
        }

        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /** @param {string} message */
  #showAddError(message) {
    const { addToCartTextError } = this.refs;
    if (!addToCartTextError) return;

    addToCartTextError.querySelector('[data-variant-update-message]')?.setAttribute('hidden', 'true');
    addToCartTextError.querySelector('[data-variant-update-retry]')?.setAttribute('hidden', 'true');
    addToCartTextError.classList.remove('hidden');
    const existingText = Array.from(addToCartTextError.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (existingText) {
      existingText.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }
    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /** @returns {AddToCartComponent[]} */
  #getAddToCartContainers() {
    return Array.from(this.querySelectorAll('add-to-cart-component'));
  }

  /** @returns {HTMLElement | undefined} */
  #getStickyAddToCart() {
    const scope = this.closest('.shopify-section');
    if (!scope) return undefined;

    const stickyBars = Array.from(scope.querySelectorAll('sticky-add-to-cart'));
    return (
      stickyBars.find(
        (element) => element instanceof HTMLElement && element.dataset.productId === this.dataset.productId
      ) ?? (stickyBars.length === 1 && stickyBars[0] instanceof HTMLElement ? stickyBars[0] : undefined)
    );
  }

  #setVariantPending() {
    for (const container of this.#getAddToCartContainers()) {
      container.setPending(true);
    }

    const stickyButton = /** @type {HTMLButtonElement | null} */ (
      this.#getStickyAddToCart()?.querySelector('[ref="addToCartButton"]') ?? null
    );
    if (stickyButton) {
      stickyButton.disabled = true;
      stickyButton.setAttribute('aria-busy', 'true');
    }

    const acceleratedCheckout = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    acceleratedCheckout?.setAttribute('hidden', 'true');
    acceleratedCheckout?.setAttribute('aria-busy', 'true');
  }

  /** @param {boolean} available */
  #applyVariantAvailability(available) {
    for (const container of this.#getAddToCartContainers()) {
      available ? container.enable() : container.disable();
    }

    const stickyAddToCart = this.#getStickyAddToCart();
    const stickyButton = /** @type {HTMLButtonElement | null} */ (
      stickyAddToCart?.querySelector('[ref="addToCartButton"]') ?? null
    );
    if (stickyAddToCart) stickyAddToCart.dataset.variantAvailable = available.toString();
    if (stickyButton) stickyButton.disabled = !available;

    const acceleratedCheckout = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    acceleratedCheckout?.toggleAttribute('hidden', !available);
  }

  #clearVariantPending() {
    for (const container of this.#getAddToCartContainers()) {
      container.setPending(false, this.#variantAvailable);
    }

    const stickyButton = this.#getStickyAddToCart()?.querySelector('[ref="addToCartButton"]');
    stickyButton?.removeAttribute('aria-busy');

    const acceleratedCheckout = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    acceleratedCheckout?.removeAttribute('aria-busy');
  }

  #clearVariantError() {
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = undefined;
    const errorElement = this.querySelector('[ref="addToCartTextError"]');
    errorElement?.classList.add('hidden');
    errorElement?.removeAttribute('aria-live');
    errorElement?.querySelector('[data-variant-update-message]')?.setAttribute('hidden', 'true');
    errorElement?.querySelector('[data-variant-update-retry]')?.setAttribute('hidden', 'true');
    this.#clearLiveRegionText();
  }

  #showVariantUpdateError() {
    const message = 'We could not update this option.';
    const errorElement = this.querySelector('[ref="addToCartTextError"]');
    if (errorElement) {
      errorElement.classList.remove('hidden');
      errorElement.setAttribute('aria-live', 'assertive');

      let messageElement = /** @type {HTMLElement | null} */ (
        errorElement.querySelector('[data-variant-update-message]')
      );
      if (!messageElement) {
        messageElement = document.createElement('span');
        messageElement.dataset.variantUpdateMessage = 'true';
        const icon = errorElement.querySelector('.svg-wrapper');
        icon ? icon.insertAdjacentElement('afterend', messageElement) : errorElement.prepend(messageElement);
      }
      messageElement.textContent = message;
      messageElement.removeAttribute('hidden');

      let retryButton = /** @type {HTMLButtonElement | null} */ (
        errorElement.querySelector('[data-variant-update-retry]')
      );
      if (!retryButton) {
        retryButton = document.createElement('button');
        retryButton.type = 'button';
        retryButton.className = 'variant-update-retry';
        retryButton.dataset.variantUpdateRetry = 'true';
        retryButton.textContent = 'Retry';
        retryButton.addEventListener('click', this.retryVariantUpdate);
        errorElement.appendChild(retryButton);
      }
      retryButton.removeAttribute('hidden');
    }
    this.#setLiveRegionText(`${message} Retry is available.`);
  }

  retryVariantUpdate = () => {
    const scope = this.closest('.shopify-section, dialog, product-card');
    const variantPickers = Array.from(scope?.querySelectorAll('variant-picker') ?? []);
    const variantPicker = /** @type {any | undefined} */ (
      variantPickers.find(
        (picker) => picker instanceof HTMLElement && picker.dataset.productId === this.dataset.productId
      ) ?? (variantPickers.length === 1 ? variantPickers[0] : undefined)
    );

    if (variantPicker?.retryLastSelection?.()) return;

    this.#setLiveRegionText('Retry is unavailable. Please refresh the page and try again.');
  };

  /**
   * @param {Document} html
   * @param {string} productId
   * @returns {HTMLElement | null}
   */
  #getUpdatedProductForm(html, productId) {
    const forms = Array.from(html.querySelectorAll('product-form-component'));
    return /** @type {HTMLElement | null} */ (
      forms.find((form) => form instanceof HTMLElement && form.dataset.productId === productId) ?? forms[0] ?? null
    );
  }

  /**
   * @param {HTMLElement} newProductForm
   * @param {string | undefined} preferredValue
   */
  #syncQuantitySelector(newProductForm, preferredValue) {
    const quantitySelector = /** @type {any | null} */ (this.querySelector('quantity-selector-component'));
    const newQuantitySelector = /** @type {HTMLElement | null} */ (
      newProductForm.querySelector('quantity-selector-component')
    );
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      quantitySelector?.querySelector('input[ref="quantityInput"]') ?? null
    );
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      newQuantitySelector?.querySelector('input[ref="quantityInput"]') ?? null
    );
    if (!quantitySelector || !newQuantitySelector || !quantityInput || !newQuantityInput) return;

    quantitySelector.dataset.variantId = newQuantitySelector.dataset.variantId ?? '';
    quantityInput.disabled = newQuantityInput.disabled;

    for (const attribute of ['data-cart-quantity', 'min', 'data-min', 'max', 'step']) {
      const value = newQuantityInput.getAttribute(attribute);
      value == null ? quantityInput.removeAttribute(attribute) : quantityInput.setAttribute(attribute, value);
    }

    const parseInteger = (value, fallback) => {
      const parsed = Number.parseInt(value ?? '', 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const min = parseInteger(newQuantityInput.min, 1);
    const step = Math.max(parseInteger(newQuantityInput.step, 1), 1);
    const cartQuantity = Math.max(parseInteger(newQuantityInput.dataset.cartQuantity, 0), 0);
    const max = newQuantityInput.hasAttribute('max') ? parseInteger(newQuantityInput.max, min) : null;
    const effectiveMax = max == null ? Infinity : Math.max(max - cartQuantity, min);
    const requestedValue = parseInteger(preferredValue, parseInteger(newQuantityInput.value, min));
    const snappedValue = min + Math.floor((requestedValue - min) / step) * step;
    quantityInput.value = Math.max(min, Math.min(effectiveMax, snappedValue)).toString();

    const newMinusButton = /** @type {HTMLButtonElement | null} */ (
      newQuantitySelector.querySelector('[ref="minusButton"]')
    );
    const newPlusButton = /** @type {HTMLButtonElement | null} */ (
      newQuantitySelector.querySelector('[ref="plusButton"]')
    );
    quantitySelector.serverDisabledMinus = newMinusButton?.disabled ?? false;
    quantitySelector.serverDisabledPlus = newPlusButton?.disabled ?? false;
    quantitySelector.updateConstraints?.(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    quantitySelector.updateButtonStates?.();

    if (quantityInput.disabled) {
      const minusButton = /** @type {HTMLButtonElement | null} */ (
        quantitySelector.querySelector('[ref="minusButton"]')
      );
      const plusButton = /** @type {HTMLButtonElement | null} */ (
        quantitySelector.querySelector('[ref="plusButton"]')
      );
      if (minusButton) minusButton.disabled = true;
      if (plusButton) plusButton.disabled = true;
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const resource = event.detail.resource;
    const productId = event.detail.data.newProduct?.id ?? event.detail.data.productId;
    const newProductForm = this.#getUpdatedProductForm(event.detail.data.html, productId);
    if (!newProductForm) {
      this.#onVariantUpdateFailure(
        new VariantUpdateFailureEvent(event.detail.sourceId, {
          productId,
          error: new Error('Variant response is missing product form markup'),
        })
      );
      return;
    }

    this.refs.variantId.value = resource?.id ?? '';
    this.#clearVariantError();

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = newProductForm.querySelector('[ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (!resource?.available) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (resource) {
      const productVariantMedia = resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;
    const currentQuantityValue = quantitySelector?.getValue?.();
    const newQuantityRules = newProductForm.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = newProductForm.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      const newProductFormButtons = newProductForm.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, newProductForm.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = newProductForm.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    this.#syncQuantitySelector(newProductForm, currentQuantityValue);

    this.#variantAvailable = resource?.available === true;
    this.#applyVariantAvailability(this.#variantAvailable);
    this.#variantChangeInProgress = false;
    this.#clearVariantPending();

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (hasB2BFeatures) void this.#fetchAndUpdateCartQuantity();
  };

  /** @param {VariantUpdateFailureEvent} event */
  #onVariantUpdateFailure = (event) => {
    if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) return;

    this.#variantAvailable = false;
    this.#applyVariantAvailability(false);
    this.#variantChangeInProgress = false;
    this.#clearVariantPending();
    this.#showVariantUpdateError();
  };

  /** @param {import('./events').VariantSelectedEvent} event */
  #onVariantSelected = (event) => {
    const productId = event.detail.resource?.productId;
    if (productId && productId !== this.dataset.productId) return;

    this.#variantChangeInProgress = true;
    this.#variantAvailable = false;
    this.#clearVariantError();
    this.#setVariantPending();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
